/* eslint-disable @next/next/no-img-element */
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import { getImage } from '@utils/imageHelper';

const Wrapper = styled.div`
  p {
    line-height: ${Fonts.HEADER_LINE_HEIGHT};
    color: var(--color-text-primary);
  }
  img {
    max-width: 100%;
    filter: invert(var(--filter));
  }
  color: var(--color-text-primary);
`;

const OptionLabel = styled.div`
  min-width: ${Spacings.SPACING_7B};
  width: ${Spacings.SPACING_7B};
  height: ${Spacings.SPACING_7B};
  color: var(--color-base-1);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-weight: ${Fonts.SEMIBOLD};
  margin-right: ${Spacings.SPACING_6B};

  img {
    min-width: ${Spacings.SPACING_7B};
    width: ${Spacings.SPACING_7B};
    height: ${Spacings.SPACING_7B};
  }

  ${({ $isSelected, $isCorrect, $isInCorrect }) => {
    if ($isCorrect && $isSelected) {
      return `
      background-color: transparent;
    `;
    }

    if ($isCorrect && !$isSelected) {
      return `
        background-color: var(--color-i-green);
    `;
    }

    if ($isInCorrect && $isSelected) {
      return `
      background-color: transparent;
    `;
    }

    if ($isSelected) {
      return `
      background-color: var(--color-text-primary);
    `;
    }

    return `
      color: var(--color-text-primary);
      background-color: var(--color-divider);
    `;
  }}
`;

const OptionContainer = styled.div`
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  cursor: ${({ $isSelectable }) => ($isSelectable ? 'pointer' : 'not-allowed')};
  display: flex;
  align-items: center;
  transition: border-color 300ms ease-in-out;

  ${({ $isSelectable, $isSelected, $isCorrect, $isInCorrect }) => {
    if ($isSelected && $isCorrect) {
      return `
        border: 1px solid var(--color-i-green);
      `;
    }

    if ($isSelected && $isInCorrect) {
      return `
        border: 1px solid var(--color-i-red);
      `;
    }

    if ($isSelected) {
      return `
        border: 1px solid var(--color-text-primary);
      `;
    }

    if (!$isSelectable) {
      return `
        border: 1px solid var(--color-divider);
      `;
    }

    return `
      border: 1px solid var(--color-divider);

      :hover {
        border-color: var(--color-text-primary);
      }
    `;
  }}
`;

const Option = ({
  uid,
  label,
  isSelectable,
  isSelected,
  isCorrect,
  isInCorrect,
  innerHTML,
  onOptionClick,
  className
}) => {
  const handleOptionClick = () => {
    if (!isSelectable) {
      return;
    }

    onOptionClick(uid);
  };

  const getLabel = () => {
    if (isCorrect && isSelected) {
      return (
        <img src={getImage('practice-session/green-right.svg')} alt="right" />
      );
    }

    if (isInCorrect && isSelected) {
      return (
        <img src={getImage('practice-session/red-wrong.svg')} alt="wrong" />
      );
    }

    return label;
  };

  return (
    <OptionContainer
      $isSelectable={isSelectable}
      $isSelected={isSelected}
      $isCorrect={isCorrect}
      $isInCorrect={isInCorrect}
      onClick={handleOptionClick}
      className={className}
    >
      <OptionLabel
        $isSelected={isSelected}
        $isCorrect={isCorrect}
        $isInCorrect={isInCorrect}
      >
        {getLabel()}
      </OptionLabel>
      <Wrapper dangerouslySetInnerHTML={innerHTML} />
    </OptionContainer>
  );
};

export default Option;

Option.propTypes = {
  uid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  isSelectable: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isCorrect: PropTypes.bool,
  isInCorrect: PropTypes.bool,
  onOptionClick: PropTypes.func.isRequired,
  innerHTML: PropTypes.shape(PropTypes.any),
  className: PropTypes.string
};

Option.defaultProps = {
  isCorrect: false,
  isInCorrect: false,
  innerHTML: {
    __html: ''
  },
  className: ''
};
